import { matchesSearch } from '@webbuilder/shared-types'
import type { ReactNode } from 'react'
/**
 * Reservas: la lista de servicios reservables.
 *
 * Cada fila es algo que se puede reservar y tiene su propio enlace público —
 * `/book/<slug>` en el sitio del cliente—, que es lo que de verdad se usa:
 * se manda por WhatsApp y ya reserva, sin montar ninguna página.
 *
 * Un servicio reservable ES una oferta: la misma que se vende en Ofertas, con
 * duración puesta. No es una tabla nueva a propósito — tener «servicios» y
 * «ofertas» por separado habría dejado dos sitios donde poner un precio.
 */
import { useI18n } from '../../../lib/i18n'
import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  IconAdjustmentsHorizontal,
  IconCalendarEvent,
  IconCheck,
  IconChevronDown,
  IconClock,
  IconCopy,
  IconDots,
  IconExternalLink,
  IconEye,
  IconLayoutGrid,
  IconPencil,
  IconPlus,
  IconSearch,
  IconSettings,
  IconUser,
  IconUsers,
  IconUsersGroup,
  IconX,
} from '@tabler/icons-react'
import { Pagination, usePaged } from '../Pagination'
import { AiPendingChip } from './AiPendingChip'
import { OfferStatusSelect } from './OfferStatusSelect'
import { minutesLabel, modeOf } from './bookingShared'
import { STATUS_KEY } from './offerShared'
import type { PublishBlock } from './offerShared'
import { useToast } from '../../Toast'
import type { Booking, Offer } from '../../../lib/api'
import { StarButton } from '../ListSelection'
import { readStarredIds, setStarred as setStarredIds } from '../../../lib/dashboardStars'
import { useSearchPlaceholder } from '../../../hooks/useSearchPlaceholder'
import { openOnRowClick } from '../rowOpen'

type StatusFilter = 'all' | 'active' | 'draft'
type ServiceSort = 'nameAsc' | 'nameDesc' | 'bookingsHigh' | 'bookingsLow' | 'priceHigh' | 'priceLow'

const PAGE_SIZE = 10

/**
 * Lo que se cobra, en una celda.
 *
 * Recibe `t` en vez de devolver una clave porque la respuesta normal NO es
 * traducible: es el importe ya formateado que viene del servidor. Sólo los dos
 * casos sin cifra —se paga allí, o no hay precio puesto— son texto nuestro.
 */
function priceOf(offer: Offer, t: (key: string) => string): string {
  if (offer.booking.paymentMode === 'onsite') return t('bookings.svc.onsite')
  const price = offer.prices.find((row) => row.isDefault && row.active) ?? offer.prices.find((row) => row.active)
  return price ? price.formatted : t('offers.noPrice')
}

export function BookingServicesPanel({
  projectId,
  services,
  bookings,
  failed,
  failureReason,
  siteOrigin,
  refreshControl, onReload,
  onOpen,
  onCreate,
  onStatusChange,
  blockFor,
  connectingStripe,
  onConnectStripe,
  onOpenPlans,
  canEditCatalog = true,
}: {
  /**
   * Si quien mira puede tocar el CATÁLOGO —crear un servicio, publicarlo o
   * pausarlo—. Un servicio reservable es una oferta, así que eso pide
   * `commerce.write`; llevar la agenda del día a día no. Sin esto, un editor
   * veía los botones y los tres devolvían 403.
   */
  canEditCatalog?: boolean
  /** Para guardar los destacados: viven por proyecto. Ver [[dashboardStars]]. */
  projectId: string
  services: Offer[]
  /** Todas las citas cargadas, para contar por servicio sin pedir de nuevo. */
  bookings: Booking[]
  failed: boolean
  failureReason: string
  /** `https://misitio.veltica.com`, o vacío si el sitio no está publicado. */
  siteOrigin: string
  refreshControl?: ReactNode
  onReload: () => void
  onOpen: (offerId: string) => void
  onCreate: () => void
  /** Publica o retira sin salir de la lista. Devuelve la oferta ya guardada. */
  onStatusChange: (service: Offer, status: 'active' | 'draft') => Promise<void>
  /** Qué impide publicar cada servicio, si es que algo lo impide. */
  blockFor: (service: Offer) => PublishBlock
  connectingStripe?: boolean | undefined
  onConnectStripe: () => void
  onOpenPlans: () => void
}) {
  const { t } = useI18n()
  const searchPlaceholder = useSearchPlaceholder('bookings.svc.searchPlaceholder')
  const { showToast } = useToast()
  const [query, setQuery] = useState('')

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  /* La estrella vive en `localStorage`, como en las demás listas, y con el
     recurso de las OFERTAS: un servicio con agenda es una oferta, así que
     destacarlo aquí lo destaca también en Cobros, y al revés. */
  const [starred, setStarred] = useState<Set<string>>(() => readStarredIds(projectId, 'offers'))
  const toggleStar = (id: string) => {
    setStarred(new Set(setStarredIds(projectId, 'offers', [id], !starred.has(id))))
  }
  const [sort, setSort] = useState<ServiceSort>('nameAsc')
  const [filterOpen, setFilterOpen] = useState(false)
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null)
  /**
   * Dónde va el menú, en coordenadas de pantalla.
   *
   * Colgaba de la propia fila, y la tabla vive dentro de un
   * `.dash-cms-table-scroll` con `overflow-x: auto`. Eso recorta también por
   * arriba y por abajo —una caja que desborda en un eje no puede dejar salir el
   * otro—, así que el menú de la última fila se veía cortado a media opción.
   *
   * Se saca al `body` con un portal y se sitúa midiendo el botón, que es lo
   * mismo que hace la lista de ofertas: misma tabla, mismo recorte, misma
   * solución. Ver `.dash-offer-row-menu-portal`.
   */
  const [listMenu, setListMenu] = useState<{ top: number; left: number; maxHeight: number; up: boolean }>({ top: 0, left: 0, maxHeight: 0, up: false })

  /* Se cierra al tocar fuera. Con el menú en el `body` ya no basta con mirar si
     el clic cayó dentro de la fila: hay que preguntar también por el portal. */
  useEffect(() => {
    if (!menuOpenId) return
    const close = (event: PointerEvent) => {
      const target = event.target as Element | null
      if (!target?.closest?.('.dash-project-row-menu-wrap') && !target?.closest?.('.dash-svc-row-menu-portal')) setMenuOpenId(null)
    }
    document.addEventListener('pointerdown', close)
    return () => document.removeEventListener('pointerdown', close)
  }, [menuOpenId])
  /** Qué fila está guardando, para no dejar pulsar dos veces el mismo estado. */
  const [busyId, setBusyId] = useState<string | null>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const filterRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!filterOpen) return
    const close = (event: PointerEvent) => {
      if (!filterRef.current?.contains(event.target as Node)) setFilterOpen(false)
    }
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setFilterOpen(false) }
    document.addEventListener('pointerdown', close)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', close)
      document.removeEventListener('keydown', onKey)
    }
  }, [filterOpen])

  /** Citas vivas por servicio: es la cifra que interesa, no el histórico. */
  const counts = useMemo(() => {
    const map = new Map<string, number>()
    for (const booking of bookings) {
      if (!booking.offerId) continue
      if (booking.status === 'cancelled' || booking.status === 'no_show') continue
      map.set(booking.offerId, (map.get(booking.offerId) ?? 0) + 1)
    }
    return map
  }, [bookings])

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const filtered = services.filter((service) => {
      if (statusFilter !== 'all' && service.status !== statusFilter) return false
      if (!needle) return true
      return matchesSearch(service.title, needle) || matchesSearch(service.slug, needle)
    })

    const amount = (service: Offer) =>
      service.prices.find((row) => row.isDefault && row.active)?.unitAmount ?? 0
    const sorted = [...filtered]
    if (sort === 'nameAsc') sorted.sort((a, b) => a.title.localeCompare(b.title, 'es'))
    else if (sort === 'nameDesc') sorted.sort((a, b) => b.title.localeCompare(a.title, 'es'))
    else if (sort === 'bookingsHigh') sorted.sort((a, b) => (counts.get(b.id) ?? 0) - (counts.get(a.id) ?? 0))
    else if (sort === 'bookingsLow') sorted.sort((a, b) => (counts.get(a.id) ?? 0) - (counts.get(b.id) ?? 0))
    else if (sort === 'priceHigh') sorted.sort((a, b) => amount(b) - amount(a))
    else sorted.sort((a, b) => amount(a) - amount(b))
    return sorted
  }, [services, query, statusFilter, sort, counts])

  const paged = usePaged(visible, PAGE_SIZE, `${query}|${statusFilter}|${sort}`)
  const filtersOn = statusFilter !== 'all'

  /* Con la lista vacía no sale la barra entera: ni buscador, ni filtros, ni
     el botón de crear. No hay nada que buscar ni que acotar, y crear ya lo
     ofrece el propio mensaje de empezar —que es lo único que tiene que
     haber en pantalla. Vuelve con la primera fila, o mientras quede algo
     escrito: si no, una búsqueda sin resultados se llevaría por delante la
     caja donde estaba escrita y no habría cómo deshacerla. */
  const showToolbar = services.length > 0 || query.trim() !== ''

  const toggleSort = (column: 'name' | 'bookings' | 'price') => {
    setSort((current) => {
      if (column === 'name') return current === 'nameAsc' ? 'nameDesc' : 'nameAsc'
      if (column === 'bookings') return current === 'bookingsHigh' ? 'bookingsLow' : 'bookingsHigh'
      return current === 'priceHigh' ? 'priceLow' : 'priceHigh'
    })
  }

  const linkFor = (service: Offer) => (siteOrigin ? `${siteOrigin}/book/${service.slug}` : '')

  const setStatus = async (service: Offer, status: 'active' | 'draft') => {
    if (busyId) return
    setBusyId(service.id)
    try {
      await onStatusChange(service, status)
    } finally {
      setBusyId(null)
    }
  }

  const copyLink = async (service: Offer) => {
    const url = linkFor(service)
    if (!url) {
      showToast(t('bookings.svc.needsPublish'), 'error')
      return
    }
    try {
      await navigator.clipboard.writeText(url)
      showToast(t('bookings.svc.linkCopied'))
    } catch {
      showToast(t('bookings.svc.copyFailed'), 'error')
    }
  }

  /* Aquí no hay espera. La lleva `BookingsSection`, que pinta el título por
     encima de esta lista: esperando sólo aquí, la cabecera salía sobre un hueco. */

  /* Fragmento y no un `div`: la barra y la tabla son hermanas dentro de la
     rejilla vertical de la pantalla —`.dash-projects-view`, con `gap: 14px`— y
     envueltas en una caja pasan a ser UN solo elemento de esa rejilla. El hueco
     entre ellas desaparecía y el buscador quedaba pegado al contenido.

     La animación de entrada la lleva la raíz de la pantalla, que es quien se
     monta al terminar la espera. */
  return (
    <>
      {!showToolbar && refreshControl ? <div className="dash-toolbar-lead">{refreshControl}</div> : null}
      {showToolbar ? (
        <header className="dash-toolbar dash-layered-toolbar">
          <div className="dash-toolbar-lead">
            {refreshControl}
          <div className="dash-toolbar-search">
            <button type="button" className="dash-toolbar-search-icon" aria-label={t('bookings.svc.searchAria')} onClick={() => searchRef.current?.focus()}>
              <IconSearch size={16} />
            </button>
            <input
              ref={searchRef}
              type="search"
              placeholder={searchPlaceholder}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => { if (event.key === 'Escape') { setQuery(''); searchRef.current?.blur() } }}
            />
            {query ? (
              <button type="button" className="dash-toolbar-search-clear" aria-label={t('bookings.clearSearch')} onClick={() => { setQuery(''); searchRef.current?.focus() }}>
                <IconX size={13} />
              </button>
            ) : null}
          </div>

          </div>

          <div className="dash-toolbar-controls">
            <div className="dash-filter-wrap" ref={filterRef}>
              <button
                className="dash-filter-trigger"
                type="button"
                aria-label={t('bookings.filtersAria')}
                data-tooltip={t('bookings.filtersAria')}
                aria-expanded={filterOpen}
                onClick={() => setFilterOpen((open) => !open)}
              >
                <IconAdjustmentsHorizontal size={15} aria-hidden="true" />
                {filtersOn ? <i className="dash-filter-active-dot" aria-hidden="true" /> : null}
              </button>
              {filterOpen ? (
                <div className="dash-filter-popover" role="dialog" aria-label={t('bookings.svc.filtersDialogAria')}>
                  <div className="dash-filter-popover-head">
                    <strong>{t('bookings.svc.filterTitle')}</strong>
                    <button type="button" aria-label={t('bookings.closeFilters')} onClick={() => setFilterOpen(false)}><IconX size={15} /></button>
                  </div>
                  <div className="dash-filter-section">
                    <span className="dash-filter-section-title">{t('bookings.filterStatus')}</span>
                    <div className="dash-filter-options">
                      {([
                        ['all', 'bookings.svc.filter.all', IconLayoutGrid],
                        ['active', 'bookings.svc.filter.active', IconEye],
                        ['draft', 'bookings.svc.filter.draft', IconPencil],
                      ] as const).map(([value, labelKey, Icon]) => (
                        <button key={value} className={statusFilter === value ? 'active' : ''} type="button" onClick={() => setStatusFilter(value)}>
                          <Icon size={14} />
                          <span>{t(labelKey)}</span>
                          {statusFilter === value ? <IconCheck size={14} /> : null}
                        </button>
                      ))}
                    </div>
                  </div>
                  {filtersOn ? (
                    <button className="dash-filter-reset" type="button" onClick={() => setStatusFilter('all')}>
                      {t('bookings.resetFilters')}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>

            {canEditCatalog ? (
            <button className="dash-primary-btn dash-toolbar-create" type="button" aria-label={t('bookings.svc.new')} onClick={onCreate}>
              <IconPlus size={15} />
              <span className="dash-btn-label dash-btn-label--desktop">{t('bookings.svc.new')}</span>
              <span className="dash-btn-label dash-btn-label--compact">{t('bookings.svc.newShort')}</span>
            </button>
            ) : null}
          </div>
        </header>
      ) : null}

      {failed ? (
        <div className="dash-inline-msg">
          <span>
            {t('bookings.svc.loadFailed')}
            {failureReason ? <code className="dash-failure-detail">{failureReason}</code> : null}
          </span>
          <div className="dash-inline-msg-actions">
            <button type="button" className="dash-secondary-btn" onClick={onReload}>{t('bookings.retry')}</button>
          </div>
        </div>
      ) : services.length === 0 ? (
        <div className="dash-placeholder">
          <IconCalendarEvent size={34} aria-hidden="true" />
          <strong>{t('bookings.svc.empty.title')}</strong>
          <span>{t('bookings.svc.empty.body')}</span>
          <button type="button" className="dash-primary-btn" onClick={onCreate}>
            <IconPlus size={15} /> {t('bookings.svc.createFirst')}
          </button>
        </div>
      ) : visible.length === 0 ? (
        <div className="dash-placeholder">
          <strong>{t('bookings.svc.noResults.title')}</strong>
          <span>{t('bookings.noResults.body')}</span>
          <button type="button" className="dash-secondary-btn" onClick={() => { setQuery(''); setStatusFilter('all') }}>{t('bookings.clear')}</button>
        </div>
      ) : (
        <>
        {/* Encima de la tabla: es donde se decide qué se va a mirar, y debajo
            obligaba a recorrer las diez filas para descubrir que había más. */}
        <Pagination page={paged.page} totalPages={paged.totalPages} total={paged.total} start={paged.start} end={paged.end} onPage={paged.setPage} unit={t('bookings.svc.unit')} />
        <div className="dash-cms-table-scroll">
          <div className="dash-data-table dash-services-data-table has-stars-only">
            <div className="dash-data-header" role="row">
              <span className="dash-data-select-cell" />
              <button className={sort.startsWith('name') ? 'is-active' : ''} type="button" onClick={() => toggleSort('name')}>
                {t('bookings.svc.col.service')} <IconChevronDown className={sort === 'nameAsc' ? 'is-asc' : ''} size={13} />
              </button>
              <button className={sort.startsWith('price') ? 'is-active' : ''} type="button" onClick={() => toggleSort('price')}>
                {t('bookings.svc.col.price')} <IconChevronDown className={sort === 'priceLow' ? 'is-asc' : ''} size={13} />
              </button>
              <span>{t('bookings.col.duration')}</span>
              <span>{t('bookings.col.mode')}</span>
              <button className={sort.startsWith('bookings') ? 'is-active' : ''} type="button" onClick={() => toggleSort('bookings')}>
                {t('bookings.col.bookings')} <IconChevronDown className={sort === 'bookingsLow' ? 'is-asc' : ''} size={13} />
              </button>
              <span>{t('bookings.col.status')}</span>
              <span className="dash-data-header-actions">{t('bookings.svc.col.actions')}</span>
            </div>

            <div className="dash-project-list">
              {paged.pageItems.map((service) => (
                <article
                  className="dash-project-row dash-data-row dash-loaded-content-in"
                  key={service.id}
                  /* Toda la ficha abre, y el toque se recoge AQUÍ y no sólo en el
                     bloque del nombre: en el teléfono la zona sensible es un
                     seudoelemento estirado sobre la fila, y un toque en un hueco de
                     ella —el padding, los datos— no llegaba a abrir nada. Los mandos
                     de dentro (estrella, estado, menú) siguen siendo suyos. */
                  onClick={openOnRowClick(() => onOpen(service.id))}
                >
                  {/* Sin casilla de selección —los servicios no se retiran en
                      bloque—, así que la celda lleva sólo la estrella, como en
                      la lista de ofertas. */}
                  <span className="dash-data-select-cell">
                    <StarButton
                      starred={starred.has(service.id)}
                      label={t('offers.starAria', { title: service.title })}
                      onToggle={() => toggleStar(service.id)}
                    />
                  </span>
                  <div
                    className="dash-project-row-main"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => { if (event.key === 'Enter') onOpen(service.id) }}
                  >
                    {service.images[0] ? (
                      <span className="dash-project-row-thumb dash-page-project-thumb">
                        <img src={service.images[0]} alt="" loading="lazy" />
                      </span>
                    ) : (
                      <span className="dash-project-row-thumb dash-page-project-thumb dash-row-glass-fallback">
                        <span className="dash-widget-glass-icon dash-sidebar-glass-icon dash-list-glass-icon is-fog" aria-hidden="true">
                          <IconCalendarEvent size={13} />
                        </span>
                      </span>
                    )}
                    <span className="dash-project-row-copy">
                      <span className="dash-project-row-title">
                        <span
                          className="dash-offer-kind-icon"
                          role="img"
                          aria-label={t('offers.kind.booking')}
                          title={t('offers.kind.booking')}
                        >
                          <IconCalendarEvent size={14} aria-hidden="true" />
                        </span>
                        <strong>{service.title}</strong>
                        <span className={`dash-booking-status dash-booking-status-compact ${service.status === 'active' ? 'is-active' : ''}`}>
                          {t(STATUS_KEY[service.status === 'active' ? 'active' : 'draft'])}
                        </span>
                      </span>
                    </span>
                  </div>

                  <div className="dash-list-row-meta dash-service-row-meta">
                    <span className="dash-data-cell" data-label={t('bookings.svc.col.price')}>{priceOf(service, t)}</span>
                    <span className="dash-data-cell" data-label={t('bookings.col.duration')}>
                      <IconClock className="dash-list-meta-icon" size={14} aria-hidden="true" />
                      {minutesLabel(service.booking.durationMinutes)}
                    </span>
                    {/* Individual o grupal, que es lo que cambia la lectura del
                        resto de la fila: en un servicio grupal las reservas son
                        personas que comparten hora, y en uno individual son
                        horas distintas. Sin esto, «12 reservas» quería decir dos
                        cosas y no había forma de saber cuál desde la lista. */}
                    <span className="dash-data-cell" data-label={t('bookings.col.mode')} title={modeOf(service.booking, t).title}>
                      {modeOf(service.booking, t).group ? (
                        <IconUsersGroup className="dash-list-meta-icon" size={14} aria-hidden="true" />
                      ) : (
                        <IconUser className="dash-list-meta-icon" size={14} aria-hidden="true" />
                      )}
                      {modeOf(service.booking, t).label}
                    </span>
                    <span className="dash-data-cell" data-label={t('bookings.col.bookings')} title={t('bookings.liveCount', { count: counts.get(service.id) ?? 0 })}>
                      <IconUsers className="dash-list-meta-icon" size={14} aria-hidden="true" />
                      {counts.get(service.id) ?? 0}
                    </span>
                    <span className="dash-data-cell dash-booking-status-cell" data-label={t('bookings.col.status')}>
                      {/* Selector si se puede cambiar, etiqueta si no. Publicar
                          era el único paso que obligaba a entrar a la ficha y es
                          el que más se repite mientras se prepara un servicio;
                          sin la variante muda el chip seguía pareciendo pulsable
                          y devolvía 403. */}
                      {service.setupPending && canEditCatalog ? (
                        <AiPendingChip compact onComplete={() => onOpen(service.id)} />
                      ) : canEditCatalog ? (
                        <OfferStatusSelect
                          compact
                          status={service.status}
                          busy={busyId === service.id}
                          blocked={blockFor(service)}
                          label={service.title}
                          {...(connectingStripe === undefined ? {} : { connecting: connectingStripe })}
                          onSelect={(status) => { void setStatus(service, status) }}
                          onConnect={onConnectStripe}
                          onOpenPlans={onOpenPlans}
                        />
                      ) : (
                        <span className={`dash-booking-status ${service.status === 'active' ? 'is-active' : ''}`}>
                          {t(STATUS_KEY[service.status === 'active' ? 'active' : 'draft'])}
                        </span>
                      )}
                    </span>
                  </div>

                  <div className="dash-project-row-actions">
                    {/* Blanco, como el «Abrir» de las ofertas: entrar en la
                        ficha de un servicio no es la acción principal de esta
                        pantalla —crear uno lo es—, y un macizo oscuro por fila
                        convierte una lista de diez en diez llamadas a la vez.
                        La misma acción en las dos listas, con el mismo botón. */}
                    <button
                      className="dash-secondary-btn dash-project-row-action dash-open-content-action"
                      type="button"
                      onClick={() => onOpen(service.id)}
                    >
                      {/* El ojo y no el calendario, igual que en Ofertas: lo
                          que se abre es la ficha del servicio, no su agenda. */}
                      <IconEye size={14} /> <span className="dash-project-row-action-label">{t('bookings.svc.open')}</span>
                    </button>
                    <div className={`dash-project-row-menu-wrap${menuOpenId === service.id ? ' is-open' : ''}`}>
                      <button
                        className={`project-menu-btn dash-icon-btn${menuOpenId === service.id ? ' active' : ''}`}
                        type="button"
                        aria-label={t('bookings.svc.optionsAria', { name: service.title })}
                        aria-expanded={menuOpenId === service.id}
                        onClick={(event) => {
                          if (menuOpenId !== service.id) {
                            /* Las medidas del menú son fijas porque sus tres
                               opciones lo son: preguntarle al DOM obligaría a
                               pintarlo antes para poder colocarlo, y eso se ve
                               como un salto. */
                            const rect = event.currentTarget.getBoundingClientRect()
                            const menuWidth = 184
                            const menuHeight = 114
                            const edge = 12
                            const gap = 8
                            const spaceBelow = window.innerHeight - rect.bottom - edge
                            const spaceAbove = rect.top - edge
                            const up = spaceBelow < menuHeight + gap && spaceAbove > spaceBelow
                            const top = up
                              ? Math.max(edge, rect.top - menuHeight - gap)
                              : Math.min(window.innerHeight - menuHeight - edge, rect.bottom + gap)
                            const left = Math.max(edge, Math.min(window.innerWidth - menuWidth - edge, rect.right - menuWidth))
                            setListMenu({ top, left, up, maxHeight: Math.max(menuHeight, up ? spaceAbove - gap : spaceBelow - gap) })
                          }
                          setMenuOpenId((id) => (id === service.id ? null : service.id))
                        }}
                      >
                        <IconDots size={16} />
                      </button>

                      {menuOpenId === service.id ? createPortal(
                        <>
                          <div className="project-menu-backdrop" role="presentation" onClick={() => setMenuOpenId(null)} />
                          <div
                            className={`project-menu dash-project-row-menu dash-svc-row-menu-portal${listMenu.up ? ' is-up' : ''}`}
                            role="menu"
                            style={{ top: listMenu.top, left: listMenu.left, maxHeight: listMenu.maxHeight, overflowY: 'auto' }}
                          >
                            <button className="project-menu-item" type="button" onClick={() => { setMenuOpenId(null); onOpen(service.id) }}><IconSettings size={14} /> {t('bookings.svc.settings')}</button>
                            <div className="project-menu-rule" />
                            {/* Apagadas hasta que hay dirección, y diciendo por
                                qué: el servicio puede estar publicado y aun así
                                no tener enlace, porque el que falta es el del
                                SITIO. Sin el motivo, «Público» y una opción
                                muerta se leen como una contradicción. */}
                            <button
                              className="project-menu-item"
                              type="button"
                              disabled={!linkFor(service) || service.status !== 'active'}
                              {...(linkFor(service) ? {} : { title: t('bookings.svc.detail.needSite') })}
                              onClick={() => { setMenuOpenId(null); window.open(linkFor(service), '_blank', 'noreferrer') }}
                            ><IconExternalLink size={14} /> {t('bookings.svc.viewPublic')}</button>
                            <button
                              className="project-menu-item"
                              type="button"
                              disabled={!linkFor(service) || service.status !== 'active'}
                              {...(linkFor(service) ? {} : { title: t('bookings.svc.detail.needSite') })}
                              onClick={() => { setMenuOpenId(null); void copyLink(service) }}
                            ><IconCopy size={14} /> {t('bookings.svc.copyLink')}</button>
                          </div>
                        </>,
                        document.body,
                      ) : null}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
        {/* Y otra vez al pie: quien acaba de recorrer la página busca el
            siguiente paso donde terminó de leer, no volviendo arriba. Con una
            sola página no se repite el mando —sólo queda la cuenta—. */}
        <Pagination page={paged.page} totalPages={paged.totalPages} total={paged.total} start={paged.start} end={paged.end} onPage={paged.setPage} unit={t('bookings.svc.unit')} alwaysShow />
        </>
      )}

    </>
  )
}
